// Presentational parts (chips, tiles, banners, header) for the
// Concept Intelligence drawer.
import React from "react";
import {
  AlertTriangle,
  CircleDot,
  Clock,
  Cpu,
  Info,
} from "lucide-react";
import {
  CONCEPT_BANNERS,
  DOC_TYPE_COLORS,
  DOC_TYPE_FALLBACK_BADGE_CLS,
  EMPTY_PLACEHOLDER,
  INSIGHT_TILE_TONES,
  type InsightTileTone,
  type StatusPillMeta,
} from "../constants";
import { formatDateTime, formatDocType, formatElapsed, pctValue, plainText } from "../utils";
import {
  CONCEPT_HEADER_AVG_MIN_WIDTH,
  confidenceTone,
} from "./utilities";

export function InsightTile({
  icon: Icon,
  value,
  label,
  hint,
  tone,
}: {
  icon: React.ComponentType<{ className?: string }>;
  value: string | number;
  label: string;
  hint?: string;
  tone: InsightTileTone;
}) {
  const toneMap = INSIGHT_TILE_TONES[tone];
  return (
    <div className="rounded-xl border border-border bg-card p-4 min-w-0 flex items-start justify-between gap-3 shadow-sm">
      <div className="min-w-0 flex flex-col gap-1.5 flex-1">
        <span className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase-safe break-words">
          {label}
        </span>
        <div className={`text-3xl font-bold tabular-nums break-words leading-none ${toneMap.value}`}>
          {value}
        </div>
        {hint && (
          <div className="text-[10.5px] text-muted-foreground break-words">
            {hint}
          </div>
        )}
      </div>
      <span
        className={`grid size-11 flex-none place-items-center rounded-xl border ${toneMap.icon}`}
      >
        <Icon className="size-5" />
      </span>
    </div>
  );
}

export function ConceptConfidenceBar({ conf }: { conf?: number | null }) {
  const c = pctValue(conf) ?? 0;
  const tone = confidenceTone(pctValue(conf));
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="relative h-1.5 flex-1 min-w-[60px] rounded-full bg-muted overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 ${tone.bar}`}
          style={{ width: `${Math.round(c * 100)}%` }}
        />
      </div>
      <span
        className={`text-[11px] font-semibold whitespace-nowrap ${tone.text} tabular-nums`}
      >
        {conf === null || conf === undefined
          ? EMPTY_PLACEHOLDER
          : (Math.round(c * 100) / 100).toFixed(2)}
      </span>
    </div>
  );
}

export function ConceptIdChip({ id }: { id: string }) {
  return (
    <span className="inline-flex items-center rounded-md bg-indigo-100 px-2 py-0.5 text-[11px] font-bold tracking-wide text-indigo-700 border border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:border-indigo-500/25 whitespace-nowrap">
      {plainText(id)}
    </span>
  );
}

export function SectionChip({ section }: { section: string | null }) {
  if (!section) {
    return (
      <span className="text-[11px] text-muted-foreground">
        {EMPTY_PLACEHOLDER}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-mono text-foreground border border-border">
      &sect;{plainText(section)}
    </span>
  );
}

export function EmptySlot({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
      <Info className="size-4" />
      No {label} extracted.
    </div>
  );
}

export function StatusBanner({ status }: { status: string }) {
  const key = status.toLowerCase();
  if (key !== "failed" && key !== "partial") return null;
  const meta = CONCEPT_BANNERS[key as "failed" | "partial"];
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border p-3 text-[12px] ${meta.cls}`}
    >
      <AlertTriangle className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">
        <div className="font-semibold">{meta.title}</div>
        <div className="mt-0.5 opacity-90">{meta.body}</div>
      </div>
    </div>
  );
}

interface RunMetaProps {
  provider: string;
  modelId: string;
  elapsedMs?: number;
  layerVersion?: number;
  createdAt?: string;
}

export function RunMetaStrip({
  provider,
  modelId,
  elapsedMs,
  layerVersion,
  createdAt,
}: RunMetaProps) {
  if (!provider && !modelId && !elapsedMs && layerVersion === undefined && !createdAt) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10.5px] text-muted-foreground border-t border-border pt-2">
      {provider && (
        <span className="inline-flex items-center gap-1">
          <Cpu className="size-3" />
          <span className="font-medium text-foreground">Provider</span>
          <span>{provider}</span>
        </span>
      )}
      {modelId && (
        <span className="inline-flex items-center gap-1">
          <span className="font-medium text-foreground">Model</span>
          <span className="font-mono">{modelId}</span>
        </span>
      )}
      {elapsedMs !== undefined && elapsedMs !== null && elapsedMs > 0 && (
        <span className="inline-flex items-center gap-1">
          <Clock className="size-3" />
          <span className="font-medium text-foreground">Ran in</span>
          <span>{formatElapsed(elapsedMs)}</span>
        </span>
      )}
      {layerVersion !== undefined && layerVersion !== null && (
        <span className="inline-flex items-center gap-1">
          <span className="font-medium text-foreground">Layer version</span>
          <span>v{layerVersion}</span>
        </span>
      )}
      {createdAt && (
        <span className="inline-flex items-center gap-1">
          <span className="font-medium text-foreground">Run at</span>
          <span>{formatDateTime(createdAt)}</span>
        </span>
      )}
    </div>
  );
}

function DocTypeBadge({ documentType }: { documentType: string }) {
  const key = documentType.toUpperCase();
  const toneCls = DOC_TYPE_COLORS[key] ?? DOC_TYPE_FALLBACK_BADGE_CLS;
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${toneCls}`}
    >
      {formatDocType(key)}
    </span>
  );
}

export interface ConceptDrawerHeaderProps {
  documentMeta: {
    title?: string;
    type?: string;
    frameworkLabel?: string;
  };
  statusMeta: {
    backendNote?: string;
    pill: StatusPillMeta;
  };
  confidenceMeta: {
    avgLabel: string;
    avgTextCls: string;
    definedCount: number;
  };
  runMeta: RunMetaProps;
}

export function ConceptDrawerHeader({
  documentMeta,
  statusMeta,
  confidenceMeta,
  runMeta,
}: ConceptDrawerHeaderProps) {
  const {
    title: documentTitle,
    type: documentType,
    frameworkLabel,
  } = documentMeta;
  const { backendNote, pill } = statusMeta;
  const { avgLabel, avgTextCls, definedCount } = confidenceMeta;

  return (
    <header className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-card p-4 pr-14 sm:p-6 sm:pr-16">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-violet-100 border border-violet-200 dark:bg-violet-500/15 dark:border-violet-500/25">
            <CircleDot className="size-5 text-violet-600 dark:text-violet-400" />
          </div>
          <div className="min-w-0 flex flex-col gap-1.5">
            <div className="text-lg font-bold text-foreground">
              Concept Intelligence
            </div>
            <p className="text-xs text-muted-foreground max-w-2xl break-words">
              Key concepts and recurring themes identified from the document, with context, provenance, and confidence.
            </p>
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              {documentTitle && (
                <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-foreground break-words">
                  {plainText(documentTitle)}
                </span>
              )}
              {documentType && <DocTypeBadge documentType={documentType} />}
              {frameworkLabel && (
                <span className="inline-flex items-center rounded-md border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground break-words">
                  Framework &middot; {plainText(frameworkLabel)}
                </span>
              )}
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${pill.cls}`}
              >
                <span className={`size-1.5 rounded-full ${pill.dot}`} />
                {pill.label}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 shrink-0">
          <div className={`flex flex-col items-end ${CONCEPT_HEADER_AVG_MIN_WIDTH}`}>
            <span className="text-[10.5px] text-muted-foreground tracking-wide">
              Defined-term confidence
            </span>
            <span className={`text-3xl font-bold tabular-nums ${avgTextCls}`}>
              {avgLabel}
            </span>
            <span className="text-[10.5px] text-muted-foreground">
              avg across {definedCount} {definedCount === 1 ? "term" : "terms"}
            </span>
          </div>
        </div>
      </div>

      <RunMetaStrip {...runMeta} />
    </header>
  );
}
